/**
 * hooks/useBranchStates.js
 *
 * The branch rows of one vendor at one location (4 Oct 2026), kept live:
 * GET /api/catalog/branches/:owner/states?location=, then every
 * `branchProduct:changed` for that vendor and location merged in as it
 * arrives (another person, or a till, changed a product there).
 *
 * Returns { states: Map<productId, StateDTO>, loading, error, refresh, merge }.
 * No row for a product means: on sale, not sold out, head office price.
 * Used by the Branches grid and by the restaurant menu's sold out control.
 */
import { useCallback, useEffect, useState } from "react";
import { apiBranchStates } from "../api/branchCatalog.js";
import useVendorOrderRooms from "./useVendorOrderRooms.js";
import { socket as sharedSocket } from "../utilities/socket";
import { branchAwareMessage, locationKeyOf } from "../utilities/vendorScope.js";

export const BRANCH_EVENT = "branchProduct:changed";

/** StateDTO rows keyed by product, for one location only. */
export const indexBranchRows = (rows, location) => {
  const key = locationKeyOf(location);
  const map = new Map();
  for (const row of rows || []) {
    if (locationKeyOf(row?.location) !== key) continue;
    map.set(String(row.productId), row);
  }
  return map;
};

export default function useBranchStates(vendorUserId, location, { socket = sharedSocket } = {}) {
  const [states, setStates] = useState(() => new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const key = location ? locationKeyOf(location) : null;

  const refresh = useCallback(async () => {
    if (!vendorUserId || !key) {
      setStates(new Map());
      return;
    }
    setLoading(true);
    setError("");
    try {
      const { data } = await apiBranchStates(vendorUserId, key);
      setStates(indexBranchRows(data?.rows, key));
    } catch (err) {
      setStates(new Map());
      setError(branchAwareMessage(err, "Branch availability could not be loaded."));
    } finally {
      setLoading(false);
    }
  }, [vendorUserId, key]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const merge = useCallback(
    (rows) =>
      setStates((prev) => {
        const next = new Map(prev);
        for (const [id, row] of indexBranchRows(rows, key)) next.set(id, row);
        return next;
      }),
    [key]
  );

  useVendorOrderRooms(socket, key ? vendorUserId : null);
  useEffect(() => {
    if (!socket || !key || !vendorUserId) return undefined;
    const onChange = (payload) => {
      const state = payload?.state;
      if (!state || String(payload?.vendorUserId || "") !== String(vendorUserId)) return;
      merge([state]);
    };
    socket.on(BRANCH_EVENT, onChange);
    return () => socket.off(BRANCH_EVENT, onChange);
  }, [socket, key, vendorUserId, merge]);

  return { states, loading, error, refresh, merge };
}
